import { Component, signal, inject, computed, OnInit, ViewChild, AfterViewInit, effect, untracked, OnDestroy, input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ApiService } from '../../../../core/services/api.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map, tap } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
  CdkDragDrop,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { IconComponent, IconName } from '../../../../shared/icons/icon.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { DropdownComponent, DropdownOption } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { BarLineComponent, BarChartData, LegendData, ColorMap, TooltipConfig, LineConfig } from '../../../../shared/components/charts/bar-charts/bar-line/bar-line.component';
import { DonutChartComponent, ChartData } from '../../../../shared/components/charts/donut-chart/donut-chart.component';
import { MultipleBarComponent, ColorMap as MultipleBarColorMap, TooltipConfig as MultipleBarTooltipConfig, LegendData as MultipleBarLegendData } from '../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component';
import { MultiLineChartComponent } from '../../../../shared/components/charts/multi-line-chart/multi-line-chart.component';
import { MultiHorizontalBarChartComponent, MultiBarData } from '../../../../shared/components/charts/multi-horizontal-bar-chart/multi-horizontal-bar-chart.component';
import { LineAreaChartComponent, ILineAreaSeries } from '../../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { RadarChartComponent, RadarData } from '../../../../shared/components/charts/radar-chart/radar-chart.component';
import { PolarAreaChartComponent, PolarChartData } from '../../../../shared/components/charts/polar-area-chart/polar-area-chart.component';
import { FunnelChartComponent, IFunnelDataPoint } from '../../../../shared/components/charts/funnel-chart/funnel-chart.component';
import { LollipopChartComponent, LollipopChartData } from '../../../../shared/components/charts/lollipop-chart/lollipop-chart.component';
import { HorizontalBarChartComponent } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { PaginatedTableComponent, TableHeader } from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { RadialPieChartComponent, RadialPieData } from '../../../../shared/components/charts/radial-pie-chart/radial-pie-chart.component';
import { DatePickerComponent, DateRangePickerOutput, TimeUnit } from '../../../../shared/components/date-picker/date-picker.component';
import { CommonService } from '../../../../core/services/common.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { AiBotService } from '../../../../core/services/ai-bot.service';
import { HeadlinePageService } from '../../../../core/services/headline-page.service';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DashboardPageKey, TrainingDashboardSectionId, TrainingDashboardSectionDef } from '../../../../core/enums/dashboard-cards.enum';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilter, findCatalogPageId, resolveActiveCatalogProductId, stripProductPrefix } from '../../../../shared/components/dashboard/dashboard-embed.helpers';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { finalize } from 'rxjs';
import { LayoutEditorOverlayDirective } from '../../../../shared/components/dashboard/layout-editor-overlay.directive';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
  id: TrainingDashboardSectionId;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
  colSpan: number;
  order: number;
}

/** Draft row used by the inline Customize Layout editor. Carries the
 *  section title alongside the persisted layout fields so the popup tile
 *  can render its label without re-looking-up `SECTIONS`. */
interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

@Component({
  selector: 'app-training-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    LayoutEditorOverlayDirective,
    IconComponent,
    // ButtonComponent,
    DropdownComponent,
    BarLineComponent,
    DonutChartComponent,
    MultipleBarComponent,
    // MultiLineChartComponent,
    MultiHorizontalBarChartComponent,
    // LineAreaChartComponent,
    // RadarChartComponent,
    PolarAreaChartComponent,
    // FunnelChartComponent,
    LollipopChartComponent,
    HorizontalBarChartComponent,
    PaginatedTableComponent,
    RadialPieChartComponent,
    DatePickerComponent,
    IndividualToasterComponent,
    DragDropModule,
    IconComponentV2,
  ],
  templateUrl: './training-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: []
})
export class TrainingDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DatePickerComponent) datePicker!: DatePickerComponent;
  private breakpointObserver = inject(BreakpointObserver);
  private apiService = inject(ApiService);
  private loaderService = inject(LoaderService);
  public _commonService = inject(CommonService);
  private productConfigService = inject(ProductConfigService);
  private aiBotService = inject(AiBotService);
  private headlinePageService = inject(HeadlinePageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private isInternalNavigation = false;
  private isInitialized = false;

  private readonly typeMap: Record<string, string> = {
    'all': '',
    'business': 'doctor',
    'partner': 'partner'
  };

  private readonly reverseTypeMap: Record<string, string> = {
    '': 'all',
    'doctor': 'business',
    'partner': 'partner'
  };
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);
  private cdr = inject(ChangeDetectorRef);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);

  readonly PAGE_KEY = DashboardPageKey.TRAINING_DASHBOARD;
  readonly SectionId = TrainingDashboardSectionId;
  readonly SECTIONS = TrainingDashboardSectionDef;


  // Date signals
  start_date = signal('2017-01-01');
  end_date = signal('2026-04-13');
  unit = signal<TimeUnit>('month');
  business_type = signal<string>('');
  topAdminsMaxVal = 0;

  // Loading signals
  isStatsLoading = signal(false);
  isPerformanceLoading = signal(false);
  isSourceLoading = signal(false);
  isTypeLoading = signal(false);
  isLeadTimeLoading = signal(false);
  isDistributionLoading = signal(false);
  isAttemptsLoading = signal(false);
  isConversionLoading = signal(false);
  isJourneyLoading = signal(false);
  isTimeToContractLoading = signal(false);
  isTopAdminsLoading = signal(false);
  isAdminTableLoading = signal(false);
  selectedProductId = signal<string | null>(null);

  isSectionLoading(id: TrainingDashboardSectionId): boolean {
    switch (id) {
      case TrainingDashboardSectionId.STATS_OVERVIEW:
        return this.isStatsLoading();
      case TrainingDashboardSectionId.PERFORMANCE_OVERVIEW:
        return this.isPerformanceLoading();
      case TrainingDashboardSectionId.SOURCE_CHANNEL_PERFORMANCE:
        return this.isSourceLoading();
      case TrainingDashboardSectionId.COMPLETION_BY_TYPE:
        return this.isTypeLoading();
      case TrainingDashboardSectionId.CANCELLATION_LEAD_TIME:
        return this.isLeadTimeLoading();
      case TrainingDashboardSectionId.STATUS_DISTRIBUTION:
        return this.isDistributionLoading();
      case TrainingDashboardSectionId.ATTEMPTS_BEFORE_COMPLETION:
        return this.isAttemptsLoading();
      case TrainingDashboardSectionId.UNREGISTERED_BOOKING_CONVERSION:
        return this.isConversionLoading();
      case TrainingDashboardSectionId.POST_REGISTRATION_JOURNEY:
        return this.isJourneyLoading();
      case TrainingDashboardSectionId.TIME_TO_FIRST_CONTRACT:
        return this.isTimeToContractLoading();
      case TrainingDashboardSectionId.TOP_ADMINS:
        return this.isTopAdminsLoading();
      case TrainingDashboardSectionId.ADMIN_CONVERSION_TABLE:
        return this.isAdminTableLoading();
      default:
        return false;
    }
  }
  isDenefits = computed(() => {
    const productId = this.selectedProductId();
    if (!productId) return false;
    const productName = this.productConfigService.getProductName(productId).toLowerCase();
    return productName === 'denefits' || productName.includes('denefits');
  });

  // Distribution Chart Signals
  distributionData = signal<ChartData[]>([]);
  totalSessions = signal<string>('0');

  constructor() {
    effect(() => {
      const globalProductId = this.apiService.selectedProductId();
      if (globalProductId) {
        untracked(() => {
          this.selectedProductId.set(globalProductId);
        });
      }
    });

    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<TrainingDashboardSectionId>();
          keys.forEach((k) => {
            if (Object.values(TrainingDashboardSectionId).includes(k as TrainingDashboardSectionId)) {
              pinned.add(k as TrainingDashboardSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const typeParam = params['type'] || 'all';
      const typeKey = `TRAINING_ANALYTICS_${typeParam}`;
      this._commonService.activeChatContextKey.set(typeKey);

      const oldType = this.business_type();
      const newType = this.typeMap[typeParam];

      if (newType !== undefined) {
        if (newType !== oldType) {
          this.business_type.set(newType);
          // If component is already initialized, fetch new data and resolve AI agent
          if (this.isInitialized) {
            this.fetchAllData();
            if (this._commonService.isPanelOpen()) {
              this.resolveAiAgent();
              const savedState = this._commonService.getChatState(typeKey);
              if (!savedState) {
                this.setAiSuggestions();
              }
            }
          }
        }
      } else {
        // Set default type=all in URL if not present
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { type: 'all' },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });

    // Hydrate layout (visibility / colSpan / order) and pinned-section
    // state from localStorage so the page can support per-card resize +
    // drag-drop reordering across reloads.
    this._initLayout();
  }

  // ─── Section layout state (visibility + colSpan + order) ──────────────────
  /** Persisted layout map driving the actual rendered page. */
  readonly layoutMap = signal<Record<TrainingDashboardSectionId, SectionLayoutItem>>(
    {} as Record<TrainingDashboardSectionId, SectionLayoutItem>,
  );

  /** All sections sorted by their persisted order. */
  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  /** Sections currently visible, in order — used by the live grid. */
  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  readonly embeddedVisibleSections = createEmbeddedSectionFilter(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.orderedSections,
  );

  /**
   * Sections currently rendered by the host. On the standalone page
   * (`mixedMode() === false`) every section is active. When mounted
   * inside `WidgetHostComponent` only the cards matching
   * `visibleWidgetKeys` are active — used to gate data loaders below.
   *
   * IMPORTANT: derived directly from `visibleWidgetKeys()` so it is
   * available synchronously on first paint; using the layout-derived
   * `embeddedVisibleSections()` would be empty before the layout loads
   * and would cause every guarded loader to short-circuit.
   */
  private readonly activeSectionIds = computed<ReadonlySet<string>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      allowed.add(k);
      const stripped = stripProductPrefix(k);
      if (stripped !== k) allowed.add(stripped);
    }
    return allowed;
  });

  private isSectionActive(...sectionIds: TrainingDashboardSectionId[]): boolean {
    if (!this.mixedMode()) return true;
    const active = this.activeSectionIds();
    if (active.size === 0) return true;
    return sectionIds.some((id) => active.has(id));
  }

  isVisible(sectionId: TrainingDashboardSectionId): boolean {
    return this.layoutMap()[sectionId]?.visible ?? true;
  }

  /** Tailwind col-span class for a section in the live 12-col grid. */
  getSectionColClass(item: SectionLayoutItem | TrainingDashboardSectionId): string {
    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'col-span-12 lg:col-span-4';
      case 6: return 'col-span-12 lg:col-span-6';
      case 8: return 'col-span-12 lg:col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  /**
   * Inner grid column class for the STATS_OVERVIEW tiles. The number of
   * stat tiles per row scales with the section's outer `colSpan` so the
   * tiles don't get squished when the user shrinks the card.
   *  - colSpan 4  → 2 tiles per row
   *  - colSpan 6  → 3 tiles per row
   *  - colSpan 8  → 4 tiles per row
   *  - colSpan 12 → default responsive ladder (up to 5 per row at 2xl)
   */
  getStatsGridColsClass(item: SectionLayoutItem | TrainingDashboardSectionId): string {
    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'grid-cols-1 sm:grid-cols-2';
      case 6: return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
      case 8: return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      case 12:
      default: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5';
    }
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  // ─── "Pin to Custom Dashboard" (API-backed) ───────────────────────────────
  readonly pinnedSections = signal<Set<TrainingDashboardSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<TrainingDashboardSectionId>>(new Set());

  isAddedToCustomDashboard(sectionId: TrainingDashboardSectionId): boolean {
    return this.pinnedSections().has(sectionId);
  }

  isAddingToCustomDashboard(sectionId: TrainingDashboardSectionId): boolean {
    return this.pendingPinSections().has(sectionId);
  }

  addToCustomDashboard(
    sec: { id: TrainingDashboardSectionId } | TrainingDashboardSectionId,
  ): void {
    // Accept either the full section layout item (templates iterating over
    // `orderedSections()` pass `sec`) or the raw section id. Both flow
    // into the same `CustomDashboardPinService.toggleSection(...)` PATCH
    // request below.
    const sectionId =
      typeof sec === 'object' && sec !== null ? sec.id : sec;
    if (this.pendingPinSections().has(sectionId)) return;
    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) return;

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    this.customDashboardPin.toggleSection({
      pageId,
      widgetKey: sectionId,
      colSpan,
      isCurrentlyPinned: this.isAddedToCustomDashboard(sectionId),
      onPinnedChange: (pinned) => {
        this.pinnedSections.update((s) => {
          const next = new Set(s);
          if (pinned) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
      },
      onPendingChange: (pending) => {
        this.pendingPinSections.update((s) => {
          const next = new Set(s);
          if (pending) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
      },
    });
  }

  // ─── Customize Layout editor (mini-dashboard preview) ─────────────────────
  /** State for the inline Customize Layout editor. */
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);

  /** Number of cards currently marked visible in the editor draft. */
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /** localStorage key for this page's persisted layout. */
  private readonly LAYOUT_LS_KEY = 'prism_training_dashboard_layout_v1';

  /** Default col-span per section — chosen so the grid mirrors the
   *  historical row pairings (full-width and 50/50 splits). */
  private readonly DEFAULT_COL_SPAN: Record<TrainingDashboardSectionId, number> = {
    [TrainingDashboardSectionId.STATS_OVERVIEW]: 12,
    [TrainingDashboardSectionId.PERFORMANCE_OVERVIEW]: 12,
    [TrainingDashboardSectionId.SOURCE_CHANNEL_PERFORMANCE]: 12,
    [TrainingDashboardSectionId.COMPLETION_BY_TYPE]: 12,
    [TrainingDashboardSectionId.CANCELLATION_LEAD_TIME]: 6,
    [TrainingDashboardSectionId.STATUS_DISTRIBUTION]: 6,
    [TrainingDashboardSectionId.ATTEMPTS_BEFORE_COMPLETION]: 6,
    [TrainingDashboardSectionId.UNREGISTERED_BOOKING_CONVERSION]: 6,
    [TrainingDashboardSectionId.POST_REGISTRATION_JOURNEY]: 6,
    [TrainingDashboardSectionId.TIME_TO_FIRST_CONTRACT]: 6,
    [TrainingDashboardSectionId.TOP_ADMINS]: 12,
    [TrainingDashboardSectionId.ADMIN_CONVERSION_TABLE]: 12,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
    }));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: TrainingDashboardSectionId): void {
    // When a card is hidden, push it to the bottom so the visible cards
    // reflow into the freed grid slot. When shown again, slot it back in
    // just above the first hidden item so it rejoins the visible group.
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
  }

  /** Restores the editor draft to the page's default order, widths, and visibility. */
  resetLayoutEditorToDefaults(): void {
    const defaults: LayoutEditorItem[] = this.SECTIONS.map((s, idx) => ({
      id: s.id as TrainingDashboardSectionId,
      title: s.title,
      visible: true,
      colSpan: this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as TrainingDashboardSectionId] ?? 12,
      ),
      order: idx,
    }));
    this.editorItems.set(defaults);
  }

  /** Persists the editor draft to local layout state + localStorage. */
  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout: Record<TrainingDashboardSectionId, SectionLayoutItem> =
      {} as Record<TrainingDashboardSectionId, SectionLayoutItem>;
    draft.forEach((d, idx) => {
      layout[d.id] = {
        id: d.id,
        visible: d.visible,
        colSpan: this._normalizeColSpan(d.colSpan),
        order: idx,
      };
    });
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);

    // Best-effort backend persistence via the shared
    // `CustomizeLayoutBatchService` — same flow as Social Platform.
    // Service no-ops silently when `pageId` / catalog isn't ready or
    // the user has no mixed dashboard yet, so existing local-only
    // behaviour is preserved.
    const pageId = this.effectiveCatalogPageId();
    if (pageId != null) {
      this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
        pageId,
        productId: resolveActiveCatalogProductId(this.apiService),
        fallbackSourcePageId: pageId,
        draft: draft.map((d, idx) => ({
          key: String(d.id),
          sourcePageId: pageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        })),
      });
    }

    this.isLayoutEditorOpen.set(false);
  }

  // ─── Editor: width chips (decrease / increase / labels) ───────────────────
  /** Steps a card down one width. Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: TrainingDashboardSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  increaseEditorCardColSpan(id: TrainingDashboardSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
  }

  /** Whether clicking Auto Adjust Width would actually grow at least one card. */
  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
  }

  /**
   * Whether the minimize button should be active for this card. A card
   * cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(
    id: TrainingDashboardSectionId,
    span: number,
  ): boolean {
    const defaultSpan = this.DEFAULT_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  /** Tailwind col-span class for an editor tile inside the popup's 12-col grid. */
  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  /** Human-friendly label for a col-span value (Small / Medium / Large / Full). */
  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }
  /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }


  // ─── Layout state private helpers ─────────────────────────────────────────
  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<TrainingDashboardSectionId, SectionLayoutItem> =
      {} as Record<TrainingDashboardSectionId, SectionLayoutItem>;

    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id as TrainingDashboardSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<TrainingDashboardSectionId, SectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable (e.g. private mode) — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.datePicker) {
        const preset = this.datePicker.predefinedRanges.find(p => p.label === 'Last 90 Days');
        if (preset) {
          this.datePicker.selectPreset(preset);
        } else {
          this.fetchAllData();
        }
      } else {
        this.fetchAllData();
      }
      this.isInitialized = true;
    }, 0);
  }

  openAiAssistant() {
    const productId = this.selectedProductId() || this.apiService.selectedProductId();
    if (!productId) return;

    this.setAiSuggestions();
    this._commonService.chatType.set('TRAINING_ANALYTICS');
    this._commonService.isPanelOpen.set(true);

    this.resolveAiAgent();
    this.fetchAllData();
  }

  private setAiSuggestions() {
    this._commonService.aiSuggestions.set([
      `Show overall training performance trends for the last 6 months`,
      `Compare cancellation rates and completion trends over the past 1 year`,
      `Show quarterly performance overview of bookings, completions, and cancellations over the past 1 year`,
      `Show time-to-first-contract distribution for the last 1 year`
    ]);
  }

  private resolveAiAgent() {
    const productId = this.selectedProductId() || this.apiService.selectedProductId();
    if (!productId) return;

    const productName = this.productConfigService.getProductName(productId).toLowerCase();
    const currentRoute = this.router.url.split('?')[0];
    const pageConfig = this.headlinePageService.getAllConfigs().find(c => currentRoute.endsWith(c.route) || currentRoute.includes(c.route));
    let pageKey = pageConfig ? pageConfig.key : '';

    if (pageKey === 'training_analytics') {
      const type = this.business_type();
      if (type === 'doctor') {
        pageKey = 'training_analytics_business';
      } else if (type === 'partner') {
        pageKey = 'training_analytics_partner';
      } else {
        pageKey = 'training_analytics_all';
      }
    }

    this.aiBotService.resolveAgent(productName, pageKey, 'ask_prism_ai').subscribe({
      next: (res: any) => {
        const agentId = res?.agent_id || res?.data?.agent_id;
        if (agentId) {
          this._commonService.activeAgentId.set(agentId);
        } else {
          this._commonService.activeAgentId.set(null);
        }
      },
      error: () => {
        this._commonService.activeAgentId.set(null);
      }
    });
  }

  ngOnDestroy(): void {
    // Only close the panel and reset context if we are actually navigating away from this page
    if (!this.isInternalNavigation) {
      this._commonService.isPanelOpen.set(false);
      this._commonService.activeAgentId.set(null);
      this._commonService.chatType.set('MONTHLY_REPORT_1');
      this._commonService.activeChatContextKey.set(null);
    }
  }

  private formatLabel(label: string): string {
    if (!label) return 'Other';
    let formatted = label.toLowerCase() === 'doctor' ? 'Business' : label;
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  private postApi(endpoint: string) {
    const formatDate = (dateStr: string) => {
      if (!dateStr) return dateStr;
      // If it's already a simple date (YYYY-MM-DD), just return it
      if (dateStr.length <= 10) return dateStr;
      // Convert UTC ISO string back to LA timezone then format
      return dayjs.utc(dateStr).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    };

    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';

    return this.apiService.post(`${toolsPath}${endpoint}`, {
      start_time: formatDate(this.start_date()),
      end_time: formatDate(this.end_date()),
      unit: this.unit(),
      type: this.business_type()
    }, {
      addAccessToken: productConfig.requiresAccessToken,
      addSecurityHeaders: false,
      withCredentials: false,
      productServerName: productConfig.productServerName
    }).pipe(
      tap({
        next: () => setTimeout(() => this.cdr.markForCheck(), 0),
        error: () => setTimeout(() => this.cdr.markForCheck(), 0)
      })
    );
  }

  fetchAllData() {
    this.fetchStats();
    this.fetchPerformanceOverview();
    this.fetchSourceChannelPerformance();
    this.fetchCompletionByType();
    this.fetchCancellationByLeadTime();
    this.fetchStatusDistribution();
    this.fetchAttemptsBeforeCompletion();
    this.fetchUnregisteredBookingConversion();
    this.fetchPostRegistrationJourney();
    this.fetchTimeToFirstContractDistribution();
    this.fetchTopAdmins();
    this.fetchFullAdminConversionTable();
  }

  fetchStats() {
    if (!this.isSectionActive(TrainingDashboardSectionId.STATS_OVERVIEW)) return;
    this.isStatsLoading.set(true);
    this.postApi('/training/stats').pipe(
      finalize(() => this.isStatsLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (res?.data) {
          const d = res.data;
          this.statsCards.set([
            {
              label: 'Total Demo Booked',
              value: d.total_booked_sessions?.toLocaleString() || '0',
              subLabel: '',
              icon: 'dollarSign',
              iconBg: 'bg-rose-500',
              shadow: 'shadow-rose-500/20'
            },
            {
              label: 'Completion Rate',
              value: `${d.completion_rate_percent || '0'}%`,
              subLabel: `${d.total_completed_sessions?.toLocaleString() || '0'} Completed`,
              icon: 'dollarSign',
              iconBg: 'bg-purple-500',
              shadow: 'shadow-purple-500/20'
            },
            {
              label: 'No Show',
              value: `${d.no_show_rate_percent || '0'}%`,
              subLabel: `${d.total_no_show_sessions?.toLocaleString() || '0'} Sessions`,
              icon: 'dollarSign',
              iconBg: 'bg-emerald-500',
              shadow: 'shadow-emerald-500/20'
            },
            {
              label: 'Cancellation Rate',
              value: `${d.cancelled_rate_percent || '0'}%`,
              subLabel: `${d.total_cancelled_sessions?.toLocaleString() || '0'} Cancelled`,
              icon: 'dollarSign',
              iconBg: 'bg-blue-500',
              shadow: 'shadow-blue-500/20'
            },
            {
              label: 'Not Interested',
              value: `${d.not_interested_rate_percent || '0'}%`,
              subLabel: `${d.total_not_interested_sessions?.toLocaleString() || '0'} Not Interested`,
              icon: 'dollarSign',
              iconBg: 'bg-amber-500',
              shadow: 'shadow-amber-500/20'
            }
          ]);
        }
      },
      error: (err) => console.error('Stats API failed', err)
    });
  }

  fetchPerformanceOverview() {
    if (!this.isSectionActive(TrainingDashboardSectionId.PERFORMANCE_OVERVIEW)) return;
    this.isPerformanceLoading.set(true);
    this.postApi('/training/performance_overview').pipe(
      finalize(() => this.isPerformanceLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          this.performanceData = res.data.map((item: any) => ({
            ...item,
            unit_date: item.unit_date || item.start || '',
            total: item.total_booked_sessions || 0,
            completion: parseFloat(item.completion_rate_percent || '0'),
            noshow: parseFloat(item.no_show_rate_percent || '0')
          }));
        }
      },
      error: (err) => console.error('Performance API failed', err)
    });
  }

  fetchSourceChannelPerformance() {
    if (!this.isSectionActive(TrainingDashboardSectionId.SOURCE_CHANNEL_PERFORMANCE)) return;
    this.isSourceLoading.set(true);
    this.postApi('/training/source_channel_performance').pipe(
      finalize(() => this.isSourceLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          this.sourceCompletionData = res.data.map((item: any) => ({
            label: this.formatLabel(item.training_source),
            total: parseFloat(item.completion_rate_percent || '0'),
            completion: parseFloat(item.completion_rate_percent || '0')
          }));
          this.sourceNoShowData = res.data.map((item: any) => ({
            label: this.formatLabel(item.training_source),
            total: parseFloat(item.no_show_rate_percent || '0'),
            noshow: parseFloat(item.no_show_rate_percent || '0')
          }));
          this.sourceCancellationData = res.data.map((item: any) => ({
            label: this.formatLabel(item.training_source),
            total: parseFloat(item.cancelled_rate_percent || '0'),
            cancellation: parseFloat(item.cancelled_rate_percent || '0')
          }));
        }
      },
      error: (err) => console.error('Source Performance API failed', err)
    });
  }

  fetchCompletionByType() {
    if (!this.isSectionActive(TrainingDashboardSectionId.COMPLETION_BY_TYPE)) return;
    this.isTypeLoading.set(true);
    this.postApi('/training/completion_by_type').pipe(
      finalize(() => this.isTypeLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          this.trainingTypeData = res.data.map((item: any) => ({
            category: this.formatLabel(item.training_type),
            groups: [
              { label: 'Completion %', value: parseFloat(item.completion_percent || '0'), color: '#A855F7' },
              { label: 'Cancellation Sessions %', value: parseFloat(item.no_show_percent || '0'), color: '#EF4444' }
            ]
          }));
        }
      },
      error: (err) => console.error('Completion Type API failed', err)
    });
  }

  fetchCancellationByLeadTime() {
    if (!this.isSectionActive(TrainingDashboardSectionId.CANCELLATION_LEAD_TIME)) return;
    this.isLeadTimeLoading.set(true);
    this.postApi('/training/cancellation_by_lead_time').pipe(
      finalize(() => this.isLeadTimeLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          const colorMap: any = {
            'same_day': '#10B981',
            'next_day': '#F59E0B',
            'within_week': '#3B82F6',
            'seven_plus_days': '#A855F7'
          };
          const labelMap: any = {
            'same_day': 'Same Day',
            'next_day': 'Next Day',
            'within_week': 'Within Week',
            'seven_plus_days': '7+ Days'
          };

          this.leadTimeData = res.data.map((item: any) => ({
            label: labelMap[item.booking_lead_time] || item.booking_lead_time,
            value: parseFloat(item.cancelled_sessions_rate_percent || '0'),
            color: colorMap[item.booking_lead_time] || '#6366F1'
          }));
        }
      },
      error: (err) => console.error('Lead Time API failed', err)
    });
  }

  fetchStatusDistribution() {
    if (!this.isSectionActive(TrainingDashboardSectionId.STATUS_DISTRIBUTION)) return;
    this.isDistributionLoading.set(true);
    this.postApi('/training/status_distribution').pipe(
      finalize(() => this.isDistributionLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          const colorMap: any = {
            'Completed': '#3B82F6',
            'No Show': '#F97316',
            'Canceled': '#EF4444',
            'Not Interested': '#A855F7',
            'Follow Up Completed': '#10B981',
            'Scheduled': '#6366F1',
            'Follow Up': '#F59E0B',
            'Rescheduled': '#EC4899'
          };

          const mappedData = res.data.map((item: any) => ({
            label: item.training_status_name,
            value: item.total_sessions,
            color: colorMap[item.training_status_name] || '#94a3b8'
          }));

          this.distributionData.set(mappedData);
          // Use overall_total_sessions from the first item
          // const total = res.data[0]?.overall_total_sessions || '0';
          const total = Number(res.data[0]?.overall_total_sessions || '0').toLocaleString('en-US')
          this.totalSessions.set(total);
        }
      },
      error: (err) => console.error('Status Distribution API failed', err)
    });
  }

  fetchAttemptsBeforeCompletion() {
    if (!this.isSectionActive(TrainingDashboardSectionId.ATTEMPTS_BEFORE_COMPLETION)) return;
    this.isAttemptsLoading.set(true);
    this.postApi('/training/attempts_before_completion').pipe(
      finalize(() => this.isAttemptsLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          const colors: any = {
            '1st attempt': '#10B981',
            '2nd attempt': '#EF4444',
            '3rd attempt': '#F59E0B',
            '4+ attempts': '#3B82F6',
            'Never Completed': '#A855F7'
          };

          this.attemptsData = res.data.map((item: any) => ({
            label: item.attempt_bucket.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            value: item.partners_count,
            color: colors[item.attempt_bucket] || '#94a3b8'
          }));
        }
      },
      error: (err) => console.error('Attempts API failed', err)
    });
  }

  fetchUnregisteredBookingConversion() {
    if (!this.isSectionActive(TrainingDashboardSectionId.UNREGISTERED_BOOKING_CONVERSION)) return;
    this.isConversionLoading.set(true);
    this.postApi('/training/unregistered_booking_conversion').pipe(
      finalize(() => this.isConversionLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          const colors: any = {
            'Still Unregistered': '#8B5CF6',
            'Converted to Doctor Registered': '#3B82F6',
            'Converted to Partner Registered': '#10B981'
          };

          const labelMap: any = {
            'Still Unregistered': 'Still Unreg',
            'Converted to Doctor Registered': 'Converted to Business Reg',
            'Converted to Partner Registered': 'Converted to Partner Reg'
          };

          this.conversionData = res.data.map((item: any) => ({
            label: labelMap[item.registration_status] || item.registration_status,
            value: item.users_count ?? item.user_count ?? 0,
            color: colors[item.registration_status] || '#94a3b8'
          }));
        }
      },
      error: (err) => console.error('Conversion API failed', err)
    });
  }

  getTotalConversion() {
    return this.conversionData.reduce((acc: number, curr: any) => acc + (curr.value || 0), 0) || 1;
  }

  fetchPostRegistrationJourney() {
    if (!this.isSectionActive(TrainingDashboardSectionId.POST_REGISTRATION_JOURNEY)) return;
    this.isJourneyLoading.set(true);
    this.postApi('/training/post_registration_journey_contract').pipe(
      finalize(() => this.isJourneyLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          const groups = ['Registered Before Training', 'Registered After Training'];

          const findMatch = (group: string, statusVariants: string[]) => {
            return res.data.find((d: any) => {
              const regJourney = d.registration_journey || d.registration_day_journey;
              const status = d.contract_status;
              if (!regJourney || !status) return false;

              const groupMatch = regJourney.toLowerCase() === group.toLowerCase();
              const statusMatch = statusVariants.some(v => v.toLowerCase() === status.toLowerCase());

              return groupMatch && statusMatch;
            });
          };

          this.registrationJourneyMadeData = groups.map(group => {
            const match = findMatch(group, ['Contract Created', 'Made contract after training']);
            const total = match ? (match.users_count ?? match.user_count ?? 0) : 0;
            return {
              label: group,
              total,
              made_contract: total,
              start: this.start_date(),
              end: this.end_date()
            };
          });
          this.registrationJourneyNoData = groups.map(group => {
            const match = findMatch(group, ['No Contract After Training', 'No contract after training']);
            const total = match ? (match.users_count ?? match.user_count ?? 0) : 0;
            return {
              label: group,
              total,
              no_contract: total,
              start: this.start_date(),
              end: this.end_date()
            };
          });
        }
      },
      error: (err) => console.error('Registration Journey API failed', err)
    });
  }

  fetchTimeToFirstContractDistribution() {
    if (!this.isSectionActive(TrainingDashboardSectionId.TIME_TO_FIRST_CONTRACT)) return;
    this.isTimeToContractLoading.set(true);
    this.postApi('/training/time_to_first_contract_distribution').pipe(
      finalize(() => this.isTimeToContractLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          const colors = ['#F59E0B', '#6366F1', '#EF4444', '#10B981', '#3B82F6', '#A855F7'];
          this.timeToFirstContractData = res.data.map((item: any, index: number) => ({
            label: item.time_to_first_contract_bucket + (item.time_to_first_contract_bucket.toLowerCase().includes('days') ? '' : ' days'),
            value: item.users_count ?? item.user_count ?? 0,
            percentage: parseFloat(item.percent_of_total || '0'),
            color: colors[index % colors.length]
          }));
        }
      },
      error: (err) => console.error('Time to Contract API failed', err)
    });
  }

  fetchTopAdmins() {
    if (!this.isSectionActive(TrainingDashboardSectionId.TOP_ADMINS)) return;
    this.isTopAdminsLoading.set(true);
    this.postApi('/training/top_admins_by_sessions_managed').pipe(
      finalize(() => this.isTopAdminsLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          this.topAdminsData = res.data.map((item: any) => ({
            label: item.admin_name,
            segments: [
              { label: 'Completion Sessions %', value: parseFloat(item.completion_rate_percent || '0'), color: '#6366F1' },
              { label: 'No Show Sessions %', value: parseFloat(item.no_show_rate_percent || '0'), color: '#A5B4FC' },
              { label: 'Cancellation Sessions %', value: parseFloat(item.cancelled_rate_percent || '0'), color: '#E0E7FF' }
            ]
          }));
          this.topAdminsMaxVal = Math.max(...this.topAdminsData.map((d: any) => d.segments.reduce((acc: number, s: any) => acc + s.value, 0))) || 100;
        }
      },
      error: (err) => console.error('Top Admins API failed', err)
    });
  }

  fetchFullAdminConversionTable() {
    if (!this.isSectionActive(TrainingDashboardSectionId.ADMIN_CONVERSION_TABLE)) return;
    this.isAdminTableLoading.set(true);
    this.postApi('/training/full_admin_conversion_table').pipe(
      finalize(() => this.isAdminTableLoading.set(false))
    ).subscribe({
      next: (res: any) => {
        if (Array.isArray(res?.data)) {
          this.adminTableData = res.data.map((item: any) => ({
            admin: item.admin_name,
            unregHandled: item.total_unreg_handled,
            convBusiness: item.conv_doctor || item.conv_business || 0,
            convPartner: item.conv_partner,
            stillUnreg: item.still_unregistered,
            convRate: item.conversion_rate + '%',
            tier: item.tier,
            tier_class: this.getTierClass(item.tier)
          }));
        }
      },
      error: (err) => console.error('Admin Table API failed', err)
    });
  }

  getTierClass(tier: string): string {
    const t = tier?.toLowerCase() || '';
    if (t.includes('top')) {
      return 'bg-emerald-50 text-emerald-500 font-semibold dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20 px-3';
    } else if (t.includes('good')) {
      return 'bg-blue-50 text-blue-500 font-semibold dark:bg-blue-500/10 dark:text-blue-400 border border-blue-100 dark:border-blue-500/20 px-3';
    } else if (t.includes('average') && !t.includes('below')) {
      return 'bg-amber-50 text-amber-500 font-semibold dark:bg-amber-500/10 dark:text-amber-400 border border-amber-100 dark:border-amber-500/20 px-3';
    } else if (t.includes('below') || t.includes('poor')) {
      return 'bg-rose-50 text-rose-500 font-semibold dark:bg-rose-500/10 dark:text-rose-400 border border-rose-100 dark:border-rose-500/20 px-3';
    }
    return 'bg-slate-50 text-slate-500 font-semibold dark:bg-slate-500/10 dark:text-slate-400 border border-slate-100 dark:border-slate-500/20 px-3';
  }

  private isInitialDateRangeLoaded = false;

  onDateRangeChange(event: DateRangePickerOutput) {
    // Skip the first emission (which defaults to 7 days) because ngAfterViewInit will trigger 90 days immediately after.
    if (!this.isInitialDateRangeLoaded && !this.mixedMode()) {
      this.isInitialDateRangeLoaded = true;
      return;
    }
    this.start_date.set(event.start_date);
    this.end_date.set(event.end_date);

    if (this.mixedMode()) {
      this.unit.set(event.unit || 'month');
    } else {
      this.unit.set('month');
    }
    this.unit.set(event.unit || 'month');

    this.fetchAllData();
  }

  isCompactView = toSignal(
    this.breakpointObserver.observe('(max-height: 610px)').pipe(map(result => result.matches)),
    { initialValue: typeof window !== 'undefined' ? window.innerHeight <= 610 : false }
  );

  isMidCompactView = toSignal(
    this.breakpointObserver.observe('(max-height: 720px)').pipe(map(result => result.matches)),
    { initialValue: typeof window !== 'undefined' ? window.innerHeight <= 720 : false }
  );

  onBusinessTypeChange(event: any) {
    this.isInternalNavigation = true;

    const typeParam = this.reverseTypeMap[event] || 'all';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { type: typeParam },
      queryParamsHandling: 'merge'
    });

    // Reset internal navigation flag after a delay to ensure ngOnDestroy 
    // captures it if a re-creation occurs, but doesn't leak for future navigations.
    setTimeout(() => {
      this.isInternalNavigation = false;
    }, 100);
  }

  // Stats Signals
  statsCards = signal([
    {
      label: 'Total Demo Booked',
      value: 'No data found',
      subLabel: '',
      icon: 'dollarSign' as IconName,
      iconBg: 'bg-rose-500',
      shadow: 'shadow-rose-500/10'
    },
    {
      label: 'Completion Rate',
      value: 'No data found',
      subLabel: '',
      icon: 'chartBar' as IconName,
      iconBg: 'bg-purple-500',
      shadow: 'shadow-purple-500/10'
    },
    {
      label: 'No Show',
      value: 'No data found',
      subLabel: '',
      icon: 'dollarSign' as IconName,
      iconBg: 'bg-emerald-500',
      shadow: 'shadow-emerald-500/10'
    },
    {
      label: 'Cancellation Rate',
      value: 'No data found',
      subLabel: '',
      icon: 'dollarSign' as IconName,
      iconBg: 'bg-blue-500',
      shadow: 'shadow-blue-500/10'
    },
    {
      label: 'Not Interested',
      value: 'No data found',
      subLabel: '',
      icon: 'dollarSign' as IconName,
      iconBg: 'bg-amber-500',
      shadow: 'shadow-amber-500/10'
    }
  ]);

  // Key Insights
  keyInsights = signal<string[]>([]);

  // Monthly Performance Chart Data
  performanceData: BarChartData[] = [];

  performanceLines: LineConfig[] = [
    { key: 'completion', label: 'Completion Rate %', color: '#F97316', axis: 'right', strokeWidth: 2, type: 'solid' },
    { key: 'noshow', label: 'No Show Rate %', color: '#A855F7', axis: 'right', strokeWidth: 2, type: 'dashed' }
  ];

  performanceLegend: LegendData[] = [
    { label: 'Total Sessions', color: '#FDBA74', type: 'circle' },
    { label: 'Completion Rate %', color: '#F97316', type: 'line' },
    { label: 'No Show Rate %', color: '#A855F7', type: 'dashed-line' }
  ];

  performanceColors: ColorMap = { data1: '#FDBA74' };

  performanceTooltip: TooltipConfig[] = [
    { label: 'Total Sessions', key: 'total', type: 'number' },
    { label: 'Completion Rate', key: 'completion', type: 'percent' },
    { label: 'No Show Rate', key: 'noshow', type: 'percent' }
  ];



  // Mock Dropdown Options
  businessTypeOptions: DropdownOption[] = [
    { label: 'Business Type', value: '' },
    { label: 'Business', value: 'doctor' },
    { label: 'Partner', value: 'partner' }
  ];

  // Performance by Source Channel Data
  sourceCompletionData: BarChartData[] = [];
  sourceNoShowData: BarChartData[] = [];
  sourceCancellationData: BarChartData[] = [];

  sourceLegend: LegendData[] = [
    { label: 'Completion %', color: '#10B981' },
    { label: 'No Show %', color: '#F97316' },
    { label: 'Cancellation %', color: '#EF4444' }
  ];

  sourceColors: any = {
    data1: '#10B981',
    data2: '#F97316',
    data3: '#EF4444'
  };

  sourceTooltip: TooltipConfig[] = [
    { label: 'Completion %', key: 'completion', type: 'percent' },
    { label: 'No Show %', key: 'noshow', type: 'percent' },
    { label: 'Cancellation %', key: 'cancellation', type: 'percent' }
  ];

  // Training Source Mix Trend Data
  trendDataAdmin: any[] = [];

  trendDataBusiness: any[] = [];

  trendDataWebsite: any[] = [];

  trendDataPartner: any[] = [];

  // Completion Rate by Training Type Data
  trainingTypeData: MultiBarData[] = [];

  // Training Activity Trend (Last 14 Days) Data
  activityTrendSeries: ILineAreaSeries[] = [];

  // Market Performance by Timezone Data
  timezoneCompletedData: BarChartData[] = [];
  timezoneNoShowData: BarChartData[] = [];

  timezoneLegend: MultipleBarLegendData[] = [
    { label: 'Completed Sessions', color: '#6366F1' },
    { label: 'No Show %', color: '#22D3EE' }
  ];

  timezoneColors: MultipleBarColorMap = {
    data1: '#6366F1',
    data2: '#22D3EE',
    data3: '',
    data4: ''
  };

  timezoneTooltip: MultipleBarTooltipConfig[] = [
    { label: 'Completed Sessions', key: 'total' },
    { label: 'No Show %', key: 'total', type: 'percent' }
  ];


  // --- NEW SECTIONS DATA ---

  // Attempts Before Completion
  attemptsData: PolarChartData[] = [];

  // Unregistered Booking Conversion
  conversionData: ChartData[] = [];

  // Heatmap Data
  heatmapDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  heatmapHours = ['7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM'];
  heatmapData: any[] = [];

  private generateHeatmapData() {
    const data: any[] = [];
    const values = [15.5, 25.5, 65.5, 95.5];
    const hours = ['7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM'];
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    hours.forEach(hour => {
      const row: any = { hour };
      days.forEach(day => {
        row[day] = values[Math.floor(Math.random() * values.length)];
      });
      data.push(row);
    });
    return data;
  }

  getHeatmapColor(value: number): string {
    if (value >= 75) return 'bg-rose-500 text-white';
    if (value >= 50) return 'bg-orange-500 text-white';
    if (value >= 25) return 'bg-orange-200 text-slate-800';
    return 'bg-orange-100 text-slate-600';
  }

  // First vs Repeat Outcomes
  firstVsRepeatCompletion: BarChartData[] = [];
  firstVsRepeatNoShow: BarChartData[] = [];
  firstVsRepeatCancellation: BarChartData[] = [];
  firstVsRepeatLegend: MultipleBarLegendData[] = [
    { label: 'Completion Rate %', color: '#6366F1' },
    { label: 'No Show Rate %', color: '#F59E0B' },
    { label: 'Cancellation Rate %', color: '#F97316' }
  ];
  firstVsRepeatColors: MultipleBarColorMap = {
    data1: '#6366F1',
    data2: '#F59E0B',
    data3: '#F97316',
    data4: ''
  };
  firstVsRepeatTooltip: MultipleBarTooltipConfig[] = [
    { label: 'Completion Rate', key: 'completion', type: 'percent' },
    { label: 'No Show Rate', key: 'noShow', type: 'percent' },
    { label: 'Cancellation Rate', key: 'cancellation', type: 'percent' }
  ];

  // Churned Partners
  churnData: RadialPieData[] = [];

  // Funnel Leakage
  funnelData: IFunnelDataPoint[] = [];

  // Funnel Leakage Bar Chart (for horizontal stacked bar view, matches Figma screenshot 2)
  funnelBarData: any[] = [];

  funnelBarLegend = [
    { label: 'Stage Value %', color: '#10B981' },
    { label: 'Stage Remaining %', color: '#A7F3D0' }
  ];

  // Booking Lead Time
  leadTimeData: LollipopChartData[] = [];

  // Top Admins by Sessions Managed
  topAdminsData: any[] = [];

  topAdminsLegend = [
    { label: 'Completion Sessions %', color: '#6366F1' },
    { label: 'No Show Sessions %', color: '#A5B4FC' },
    { label: 'Cancellation Sessions %', color: '#E0E7FF' }
  ];

  // Admin Conversion Table
  adminTableHeaders: TableHeader[] = [
    { label: 'Admin', key: 'admin', align: 'text-left' },
    { label: 'Total Unreg. Handled', key: 'unregHandled', align: 'text-left' },
    { label: 'Conversion Business', key: 'convBusiness', align: 'text-left' },
    { label: 'Conversion Partner', key: 'convPartner', align: 'text-left' },
    { label: 'Still Unregistered', key: 'stillUnreg', align: 'text-left' },
    { label: 'Conversion Rate', key: 'convRate', align: 'text-left' },
    { label: 'Tier', key: 'tier', align: 'text-left', cellType: 'badge' }
  ];


  registrationJourneyMadeData: BarChartData[] = [];
  registrationJourneyNoData: BarChartData[] = [];
  registrationJourneyLegend: LegendData[] = [
    { label: 'Made Contract After Training', color: '#6366F1' },
    { label: 'No Contract After Training', color: '#F59E0B' }
  ];
  registrationJourneyColors: any = {
    data1: '#6366F1',
    data2: '#F59E0B'
  };
  registrationJourneyTooltip: TooltipConfig[] = [
    { label: 'Made Contract After Training', key: 'made_contract', type: 'number' },
    { label: 'No Contract After Training', key: 'no_contract', type: 'number' }
  ];

  // Time to First Contract Distribution
  timeToFirstContractData: RadialPieData[] = [];

  adminTableData: any[] = [];
}
